import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Mic, Square, Loader2, Download, Lock, AlertTriangle, RefreshCw, Languages, CloudUpload, WifiOff } from "lucide-react";
import { toast } from "sonner";
import Orb from "@/components/Orb";
import { useAuth } from "@/context/AuthContext";

// Opus mono @ 64 kbps ≈ 28 MB / hour — the server splits long files, so we can afford
// a higher bitrate than before, which measurably improves transcription accuracy.
const RECORDING_BITRATE = 64000;
const TIMESLICE_MS = 15000;      // stream a chunk to the server every 15s
const STALL_MS = 50000;          // no audio data for this long = the OS killed our capture

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return "";
  for (const t of MIME_CANDIDATES) {
    try { if (MediaRecorder.isTypeSupported(t)) return t; } catch { /* ignore */ }
  }
  return "";
};

const extFor = (mime) => {
  const m = (mime || "").toLowerCase();
  if (m.includes("mp4") || m.includes("aac")) return ".m4a";
  if (m.includes("ogg")) return ".ogg";
  if (m.includes("mpeg")) return ".mp3";
  return ".webm";
};

const Recorder = ({ onUploaded, currentSubject }) => {
  const { user, refresh } = useAuth();
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [subjectOverride, setSubjectOverride] = useState("");
  const [stream, setStream] = useState(null);
  const [lastBlob, setLastBlob] = useState(null);
  const [languages, setLanguages] = useState([{ code: "auto", name: "Auto-detect" }]);
  const [language, setLanguage] = useState(user?.preferred_language || "auto");
  const [syncedBytes, setSyncedBytes] = useState(0);
  const [stalled, setStalled] = useState(false);
  const [offlineChunks, setOfflineChunks] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);          // local copy (rescue download)
  const startRef = useRef(0);
  const intervalRef = useRef(null);
  const streamRef = useRef(null);
  const sessionRef = useRef(null);       // { id, ext, mime, subject, language }
  const indexRef = useRef(0);
  const queueRef = useRef(Promise.resolve());
  const pendingRef = useRef([]);         // chunks the server never accepted
  const lastDataRef = useRef(0);
  const wakeLockRef = useRef(null);
  const keepAliveRef = useRef(null);
  const stalledToastRef = useRef(false);

  useEffect(() => () => clearInterval(intervalRef.current), []);
  useEffect(() => () => { if (lastBlob?.url) URL.revokeObjectURL(lastBlob.url); }, [lastBlob]);
  useEffect(() => { setLanguage(user?.preferred_language || "auto"); }, [user?.preferred_language]);
  useEffect(() => {
    let cancelled = false;
    api.get("/languages").then(({ data }) => { if (!cancelled) setLanguages(data.languages || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const limit = user?.plan === "pro" ? Infinity : user?.plan === "starter" ? 50 : 5;
  const used = user?.usage?.recordings || 0;
  const atLimit = used >= limit;

  // ---- screen wake lock: stops Android/iOS suspending the tab mid-lesson ----
  const acquireWakeLock = async () => {
    try {
      if ("wakeLock" in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
        wakeLockRef.current.addEventListener?.("release", () => { wakeLockRef.current = null; });
      }
    } catch { /* unsupported or denied — recording still works */ }
  };
  const releaseWakeLock = () => {
    try { wakeLockRef.current?.release?.(); } catch { /* ignore */ }
    wakeLockRef.current = null;
  };
  useEffect(() => {
    const onVis = () => { if (recording && document.visibilityState === "visible" && !wakeLockRef.current) acquireWakeLock(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [recording]);

  // silent audio graph keeps the browser's audio session alive in the background
  const startKeepAlive = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      ctx.resume?.();
      keepAliveRef.current = { ctx, osc };
    } catch { /* ignore */ }
  };
  const stopKeepAlive = () => {
    try { keepAliveRef.current?.osc?.stop(); keepAliveRef.current?.ctx?.close(); } catch { /* ignore */ }
    keepAliveRef.current = null;
  };

  // ---- chunk streaming ----
  const uploadChunkWithRetry = async (sid, index, blob, attempts = 3) => {
    for (let i = 0; i < attempts; i++) {
      try {
        const fd = new FormData();
        fd.append("chunk", blob, `chunk-${index}`);
        await api.post(`/recordings/session/${sid}/chunk?index=${index}`, fd);
        return true;
      } catch (err) {
        if (err?.response?.status === 413 || err?.response?.status === 404) return false;
        await new Promise((r) => setTimeout(r, 1500 * (i + 1)));
      }
    }
    return false;
  };

  const enqueueChunk = (sid, index, blob) => {
    queueRef.current = queueRef.current.then(async () => {
      const ok = await uploadChunkWithRetry(sid, index, blob);
      if (ok) setSyncedBytes((b) => b + blob.size);
      else { pendingRef.current.push({ index, blob }); setOfflineChunks((n) => n + 1); }
    });
  };

  const flushQueue = async () => {
    await queueRef.current;
    const sid = sessionRef.current?.id;
    if (!sid || pendingRef.current.length === 0) return;
    const retries = [...pendingRef.current];
    pendingRef.current = [];
    setOfflineChunks(0);
    for (const c of retries) {
      const ok = await uploadChunkWithRetry(sid, c.index, c.blob, 2);
      if (ok) setSyncedBytes((b) => b + c.blob.size);
      else { pendingRef.current.push(c); setOfflineChunks((n) => n + 1); }
    }
  };

  const start = async () => {
    if (atLimit) {
      toast.error("You've hit this month's recording limit. Upgrade to keep recording.");
      return;
    }
    let s;
    try {
      s = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch {
      try { s = await navigator.mediaDevices.getUserMedia({ audio: true }); }
      catch { toast.error("Microphone permission required"); return; }
    }

    const mime = pickMime();
    const ext = extFor(mime);

    // Reserve a server-side session first so the quota is checked BEFORE a long lesson.
    let session = null;
    try {
      const { data } = await api.post("/recordings/session/start", {
        language: language !== "auto" ? language : null,
        subject_override: subjectOverride || null,
        ext,
      });
      session = { id: data.session_id, ext, mime, subject: subjectOverride || currentSubject || "Untitled", language };
    } catch (err) {
      if (err?.response?.status === 402) {
        s.getTracks().forEach((t) => t.stop());
        toast.error(err?.response?.data?.detail || "Recording limit reached");
        try { await refresh(); } catch { /* ignore */ }
        return;
      }
      toast.message("Offline mode — audio is kept on this device and uploaded when you stop");
    }

    let mr;
    try { mr = new MediaRecorder(s, mime ? { mimeType: mime, audioBitsPerSecond: RECORDING_BITRATE } : { audioBitsPerSecond: RECORDING_BITRATE }); }
    catch {
      try { mr = new MediaRecorder(s, mime ? { mimeType: mime } : {}); }
      catch { s.getTracks().forEach((t) => t.stop()); toast.error("This browser can't record audio"); return; }
    }

    streamRef.current = s;
    setStream(s);
    sessionRef.current = session;
    chunksRef.current = [];
    pendingRef.current = [];
    indexRef.current = 0;
    queueRef.current = Promise.resolve();
    stalledToastRef.current = false;
    setSyncedBytes(0);
    setOfflineChunks(0);
    setStalled(false);
    lastDataRef.current = Date.now();

    mr.ondataavailable = (e) => {
      if (!e.data || e.data.size === 0) return;
      lastDataRef.current = Date.now();
      setStalled(false);
      chunksRef.current.push(e.data);
      const sid = sessionRef.current?.id;
      if (sid) enqueueChunk(sid, indexRef.current++, e.data);
    };
    mr.onerror = () => toast.error("The browser stopped the recording — tap stop to save what was captured");
    mr.onstop = handleStop;

    // if the OS revokes the mic (call, screen lock on some Androids) — salvage immediately
    s.getAudioTracks().forEach((t) => {
      t.onended = () => { toast.error("Microphone was cut off — saving what we captured"); stop(); };
    });

    mediaRecorderRef.current = mr;
    mr.start(TIMESLICE_MS);
    await acquireWakeLock();
    startKeepAlive();
    setRecording(true);
    startRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
      if (Date.now() - lastDataRef.current > STALL_MS) {
        setStalled(true);
        if (!stalledToastRef.current) {
          stalledToastRef.current = true;
          toast.error("No audio is reaching us — keep this screen open, then tap stop to save.", { duration: 10000 });
        }
      }
    }, 1000);
  };

  const stop = () => {
    const mr = mediaRecorderRef.current;
    if (!mr || mr.state === "inactive") return;
    try { mr.requestData?.(); } catch { /* ignore */ }
    mr.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
    setRecording(false);
    clearInterval(intervalRef.current);
    releaseWakeLock();
    stopKeepAlive();
  };

  // Legacy single-shot upload (offline mode / server session unavailable)
  const uploadWhole = async ({ blob, filename, subject, duration, language: lang }) => {
    const fd = new FormData();
    fd.append("audio", blob, filename);
    if (subject) fd.append("subject_override", subject);
    fd.append("duration_seconds", String(duration || 0));
    if (lang && lang !== "auto") fd.append("language", lang);
    const { data } = await api.post("/recordings", fd);
    return data;
  };

  const finishSession = async (session, duration) => {
    const { data } = await api.post(`/recordings/session/${session.id}/finish`, {
      duration_seconds: duration || 0,
      subject_override: session.subject && session.subject !== "Untitled" ? session.subject : null,
      language: session.language !== "auto" ? session.language : null,
    });
    return data;
  };

  const handleStop = async () => {
    const session = sessionRef.current;
    const mime = session?.mime || pickMime() || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: mime.split(";")[0] });
    const ext = extFor(mime);
    const filename = `vertisiya-${new Date().toISOString().replace(/[:.]/g, "-")}${ext}`;
    const subject = subjectOverride || currentSubject || "Untitled";
    const duration = elapsed;
    const lang = language;

    setUploading(true);
    try {
      if (session) {
        await flushQueue();
        let data;
        try {
          data = await finishSession({ ...session, subject }, duration);
        } catch (e) {
          // server session unusable (e.g. no chunks arrived) — fall back to uploading the local copy
          if (blob.size === 0) throw e;
          data = await uploadWhole({ blob, filename, subject, duration, language: lang });
        }
        toast.success(`Saved to ${data.subject}`);
        setElapsed(0);
        setSubjectOverride("");
        if (lastBlob?.url) URL.revokeObjectURL(lastBlob.url);
        setLastBlob(null);
        sessionRef.current = null;
        onUploaded?.(data);
        return;
      }
      if (blob.size === 0) throw new Error("Nothing was captured");
      const data = await uploadWhole({ blob, filename, subject, duration, language: lang });
      toast.success(`Saved to ${data.subject}`);
      setElapsed(0);
      setSubjectOverride("");
      onUploaded?.(data);
      return;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Upload failed";
      const url = URL.createObjectURL(blob);
      setLastBlob({ blob, url, filename, subject, duration, language: lang, reason: msg, session });
      toast.error(msg + " · your recording is safe below", { duration: 9000 });
      if (err?.response?.status === 402) { try { await refresh(); } catch { /* ignore */ } }
    } finally {
      setUploading(false);
    }
  };

  const retryUpload = async () => {
    if (!lastBlob) return;
    setUploading(true);
    try {
      let data;
      if (lastBlob.session) {
        await flushQueue();
        try {
          data = await finishSession({ ...lastBlob.session, subject: lastBlob.subject }, lastBlob.duration);
        } catch (e) {
          if (lastBlob.blob.size === 0) throw e;
          data = await uploadWhole(lastBlob);
        }
      } else {
        data = await uploadWhole(lastBlob);
      }
      toast.success(`Saved to ${data.subject}`);
      URL.revokeObjectURL(lastBlob.url);
      setLastBlob(null);
      setElapsed(0);
      sessionRef.current = null;
      onUploaded?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Still failing — download the audio to keep it safe");
    } finally {
      setUploading(false);
    }
  };

  const discardLast = () => {
    if (lastBlob?.url) URL.revokeObjectURL(lastBlob.url);
    if (lastBlob?.session?.id) api.post(`/recordings/session/${lastBlob.session.id}/abort`).catch(() => {});
    setLastBlob(null);
    toast.message("Discarded");
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const state = recording ? "Recording" : uploading ? "Processing" : atLimit ? "Locked" : "Ready";
  const syncedMB = (syncedBytes / 1024 / 1024).toFixed(1);

  return (
    <div className="glass-strong p-8 relative overflow-hidden" data-testid="recorder">
      <div className="flex items-start justify-between mb-6">
        <div className="label-mono flex items-center gap-3">
          {recording && <span className="pulse-dot" />}
          {atLimit && !recording && !uploading && <Lock size={11} className="text-amber-500" />}
          {state}
        </div>
        <div className="text-[0.7rem] text-neutral-500">
          Auto-folder · <span className="text-white">{currentSubject || "Untitled"}</span>
        </div>
      </div>

      <div className="flex items-center gap-8">
        <div className="relative shrink-0">
          <Orb size={168} active={recording} stream={stream} />
          <button
            onClick={recording ? stop : start}
            disabled={uploading || (!recording && atLimit)}
            data-testid={recording ? "stop-recording" : "start-recording"}
            aria-label={recording ? "Stop recording" : atLimit ? "Recording limit reached" : "Start recording"}
            className="absolute inset-0 flex items-center justify-center transition-transform hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span className="w-14 h-14 rounded-full bg-black/70 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,0.6)]">
              {uploading ? (
                <Loader2 className="animate-spin text-white" size={22} />
              ) : recording ? (
                <Square className="text-white" size={18} fill="currentColor" />
              ) : atLimit ? (
                <Lock className="text-white/70" size={20} />
              ) : (
                <Mic className="text-white" size={22} />
              )}
            </span>
          </button>
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-5xl md:text-6xl tightest tabular text-white leading-none mb-3">
            {fmt(elapsed)}
          </div>
          <div className="label-mono mb-4">
            {recording
              ? "listening · streaming to vertisiya"
              : atLimit
                ? `${used}/${limit === Infinity ? "∞" : limit} used this month`
                : "tap the mic to begin"}
          </div>
          {!recording && !atLimit && (
            <div className="space-y-2">
              <input
                className="input-cyber"
                placeholder="Override subject (optional, e.g. Chemistry)"
                value={subjectOverride}
                onChange={(e) => setSubjectOverride(e.target.value)}
                data-testid="subject-override"
              />
              <div className="flex items-center gap-2 text-[0.72rem]">
                <Languages size={12} className="text-neutral-500 shrink-0" />
                <span className="text-neutral-500">Language</span>
                <select
                  className="bg-transparent border border-white/[0.08] rounded-[2px] px-2 py-1 text-[0.75rem] text-white focus:outline-none focus:border-white/25 hover:border-white/15 transition-colors"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  data-testid="recording-language"
                >
                  {languages.map((l) => (
                    <option key={l.code} value={l.code} className="bg-black">{l.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {!recording && atLimit && (
            <Link to="/pricing" data-testid="recorder-upgrade">
              <button className="btn-primary">Upgrade to keep recording</button>
            </Link>
          )}
          {recording && (
            <div className="space-y-1.5" data-testid="sync-status">
              <div className="flex items-center gap-2 text-[0.72rem] font-mono text-neutral-400">
                <CloudUpload size={12} className={stalled ? "text-rose-400" : "text-emerald-400"} />
                {syncedMB} MB safe on the server
                {offlineChunks > 0 && (
                  <span className="text-amber-400 flex items-center gap-1"><WifiOff size={11}/> {offlineChunks} queued</span>
                )}
              </div>
              <div className="text-[0.7rem] text-neutral-500">
                {stalled
                  ? "audio stream stalled — keep this screen on"
                  : "keep the screen on · we save every 15 seconds"}
              </div>
            </div>
          )}
        </div>
      </div>

      {lastBlob && (
        <div className="mt-6 pt-6 border-t border-white/[0.06]" data-testid="unsaved-recording">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-8 h-8 border border-amber-500/40 bg-amber-500/[0.08] flex items-center justify-center rounded-[2px] shrink-0">
              <AlertTriangle size={14} className="text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[0.9rem] font-medium mb-1">Recording not saved to Vertisiya</div>
              <div className="text-[0.78rem] text-neutral-400 leading-relaxed break-words">
                {lastBlob.reason}. Your audio is <span className="text-white">safe on this device</span> — download it or retry once the issue is resolved.
              </div>
              <div className="text-[0.72rem] text-neutral-500 mt-2 font-mono">
                {lastBlob.subject} · {fmt(lastBlob.duration)} · {(lastBlob.blob.size / 1024 / 1024).toFixed(1)} MB local
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={lastBlob.url} download={lastBlob.filename} data-testid="download-unsaved">
              <button className="btn-primary text-[0.82rem] py-2 px-4">
                <Download size={13}/> Download audio
              </button>
            </a>
            <button
              onClick={retryUpload}
              disabled={uploading}
              className="btn-secondary text-[0.82rem] py-2 px-4"
              data-testid="retry-unsaved"
            >
              {uploading ? <><Loader2 size={13} className="animate-spin"/> Retrying…</> : <><RefreshCw size={13}/> Retry save</>}
            </button>
            <button onClick={discardLast} className="btn-ghost text-[0.82rem] py-2 px-3" data-testid="discard-unsaved">
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recorder;
